import axios from '@/services/axios';
import { useUserStore } from '@/stores/user';

const workspaceRoute = (route, workspaceId) => {
  const userStore = useUserStore();
  return `workspaces/${workspaceId || userStore.workspace.id}/${route}`;
};

const organizationRoute = (route, organizationId) => {
  const userStore = useUserStore();
  return `organizations/${organizationId || userStore.workspace.organizationId}/${route}`;
};

const api = {
  auth: {
    async login(payload) {
      return axios.post('auth/login', payload);
    },
    async register(payload) {
      return axios.post('auth/signup', payload);
    },
    async registerVerificationRequest(payload) {
      return axios.post('auth/verify-email', payload);
    },
    async googleAuth(payload) {
      return axios.post('auth/google/url', payload);
    },
    async resetPasswordRequest(payload) {
      return axios.post('auth/password-reset/request', payload);
    },
    async resetPasswordVerifyCode(payload) {
      return axios.post('auth/password-reset/verify-code', payload);
    },
    async resetPasswordChangePassword(payload) {
      return axios.post('auth/password-reset/change-password', payload);
    }
  },
  user: {
    me() {
      return axios.get('users/me');
    },
    updateUser(payload) {
      return axios.put('users/me', payload);
    },
    updateSettings(payload) {
      return axios.patch('users/settings', payload);
    },
    changePassword(payload) {
      return axios.post('users/change-password', payload);
    },
    upload(payload) {
      return axios.post('users/upload', payload);
    },
  },
  billing: {
    subscription: {
      get() {
        return axios.get(organizationRoute('subscriptions'));
      },
      update(payload) {
        return axios.post(organizationRoute('subscriptions/update'), payload);
      },
      redeemCoupon(payload) {
        return axios.post(organizationRoute('subscriptions/redeem-coupon'), payload);
      },
      applyTrialPromo() {
        return axios.get(organizationRoute('subscriptions/trial-promo'));
      },
      session(payload) {
        return axios.post(organizationRoute('subscriptions/session'), payload);
      },
      buyCredits(payload) {
        return axios.post(organizationRoute('subscriptions/buy-credits'), payload);
      },
      activatePlan() {
        return axios.post(organizationRoute('subscriptions/pay-now'));
      },
      cancel(payload) {
        return axios.post(organizationRoute('subscriptions/cancel'), payload);
      }
    },
    paymentMethods: {
      getAll() {
        return axios.get(organizationRoute('payment-methods'));
      },
      create(payload) {
        return axios.post(organizationRoute('payment-methods/card-checkout-session'), payload);
      },
      setDefault(id) {
        return axios.post(organizationRoute(`payment-methods/${id}/default`));
      },
      delete(id) {
        return axios.delete(organizationRoute(`payment-methods/${id}`));
      },
    },
    invoices: {
      getAll() {
        return axios.get(organizationRoute('invoices'));
      },
      getUpcomingOverages() {
        return axios.get(organizationRoute('invoices/upcoming/overages'));
      },
      getUpcomingPlan() {
        return axios.get(organizationRoute('invoices/upcoming/plan'));
      }
    },
    plans: {
      getAll() {
        return axios.get(organizationRoute('plans'));
      },
    }
  },
  usage: {
    get() {
      return axios.get(organizationRoute('usage/get'));
    }
  },
  organizations: {
    update(organizationId, payload) {
      return axios.patch(organizationRoute('update', organizationId), payload);
    },
  },
  workspaces: {
    create(organizationId, payload) {
      return axios.post(organizationRoute('workspaces/create', organizationId), payload);
    },
    update(id, payload) {
      return axios.post(organizationRoute(`workspaces/${id}/update`), payload);
    },
    delete(id) {
      return axios.delete(organizationRoute(`workspaces/${id}/delete`));
    },
  },
  users: {
    getAll() {
      return axios.get(organizationRoute('users/get'));
    },
    update(id, payload) {
      return axios.patch(organizationRoute(`users/${id}/update`), payload);
    },
    delete(id) {
      return axios.delete(organizationRoute(`users/${id}/delete`));
    },
  },
  invitations: {
    getAll() {
      return axios.get(organizationRoute('invitations/get'));
    },
    create(payload) {
      return axios.post(organizationRoute('invitations/create'), payload);
    },
    update(id, payload) {
      return axios.patch(organizationRoute(`invitations/${id}/update`), payload);
    },
    delete(id) {
      return axios.delete(organizationRoute(`invitations/${id}/delete`));
    },
  },
  globalDomains: {
    getAll() {
      return axios.get(organizationRoute('domains/get'));
    },
    check() {
      return axios.get(organizationRoute('domains/check'));
    },
    create(payload) {
      return axios.post(organizationRoute('domains/create'), payload);
    },
    update(id, payload) {
      return axios.patch(organizationRoute(`domains/${id}/update`), payload);
    },
    delete(id) {
      return axios.delete(organizationRoute(`domains/${id}/delete`));
    },
  },
  globalLeads: {
    getAll({
      startDate, endDate, timezone, page, limit, q, workspaceIds, status
    }) {
      return axios.get(organizationRoute('leads/get'), {
        params: {
          startDate, endDate, timezone, page, limit, q, workspaceIds, status
        },
      });
    },
    getAllForExport({
      startDate, endDate, timezone, q, variantIds, landerIds, workspaceIds, status
    }) {
      return axios.get(organizationRoute('leads/getAll'), {
        params: {
          startDate, endDate, timezone, q, variantIds, landerIds, workspaceIds, status
        },
      });
    },
    update(payload) {
      return axios.put(organizationRoute('leads/update'), payload);
    },
    delete(payload) {
      return axios.post(organizationRoute('leads/delete'), payload);
    },
  },
  credits: {
    customRequest(payload) {
      return axios.post(workspaceRoute('credits/request'), payload);
    },
  },
  dashboard: {
    getStats({ startDate, endDate, timezone }) {
      return axios.get(workspaceRoute('dashboard/get'), { params: { startDate, endDate, timezone } });
    },
  },
  reporting: {
    getGraphs({
      startDate, endDate, timezone, landerIds
    }) {
      return axios.get(workspaceRoute('reporting/get'), {
        params: {
          startDate, endDate, timezone, landerIds
        }
      });
    }
  },
  folders: {
    getAll() {
      return axios.get(workspaceRoute('folders/get'));
    },
    create(payload) {
      return axios.post(workspaceRoute('folders/create'), payload);
    },
    update(id, payload) {
      return axios.post(workspaceRoute(`folders/${id}/update`), payload);
    },
    moveToWorkspace(id, payload) {
      return axios.post(workspaceRoute(`folders/${id}/move-to-workspace`), payload);
    },
    delete(id) {
      return axios.delete(workspaceRoute(`folders/${id}/delete`));
    },
  },
  landingPages: {
    getAll() {
      return axios.get(workspaceRoute('landers/get'));
    },
    get(id) {
      return axios.get(workspaceRoute(`landers/${id}/get`));
    },
    getAnalytics({ startDate, endDate, timezone }) {
      return axios.get(workspaceRoute('landers/analytics/get'), { params: { startDate, endDate, timezone } });
    },
    search({
      page, limit, q, folderId, websiteId
    }) {
      return axios.get(workspaceRoute('landers/search'), {
        params: {
          page, limit, q, folderId, websiteId
        }
      });
    },
    create(payload, type) {
      return axios.post(workspaceRoute(`landers/create${type ? `/${type}` : ''}`), payload);
    },
    update(id, payload) {
      return axios.put(workspaceRoute(`landers/${id}/update`), payload);
    },
    clone(id, payload) {
      return axios.post(workspaceRoute(`landers/${id}/clone`), payload);
    },
    publish(id, payload) {
      return axios.post(workspaceRoute(`landers/${id}/publish`), payload);
    },
    unpublish(id) {
      return axios.post(workspaceRoute(`landers/${id}/unpublish`));
    },
    download(id) {
      return axios.get(workspaceRoute(`landers/${id}/download`), { responseType: 'blob' });
    },
    moveToWorkspace(id, payload) {
      return axios.post(workspaceRoute(`landers/${id}/move-to-workspace`), payload);
    },
    moveToFolder(payload) {
      return axios.post(workspaceRoute('landers/move-to-folder'), payload);
    },
    removeFromFolder(id) {
      return axios.post(workspaceRoute(`landers/${id}/remove-from-folder`));
    },
    addToTracker(id, integrationId, payload) {
      return axios.post(workspaceRoute(`landers/${id}/integrations/${integrationId}/publish`), payload);
    },
    delete(id) {
      return axios.delete(workspaceRoute(`landers/${id}/delete`));
    },
  },
  websites: {
    getAll() {
      return axios.get(workspaceRoute('websites/get'));
    },
    get(id) {
      return axios.get(workspaceRoute(`websites/${id}/get`));
    },
    create(payload) {
      return axios.post(workspaceRoute('websites/create'), payload);
    },
    update(id, payload) {
      return axios.post(workspaceRoute(`websites/${id}/update`), payload);
    },
    publish(id, payload) {
      return axios.post(workspaceRoute(`websites/${id}/publish`), payload);
    },
    unpublish(id) {
      return axios.post(workspaceRoute(`websites/${id}/unpublish`));
    },
    setMaster(id, landerId) {
      return axios.post(workspaceRoute(`websites/${id}/landers/${landerId}/setMaster`));
    },
    delete(id) {
      return axios.delete(workspaceRoute(`websites/${id}/delete`));
    },
  },
  quizzes: {
    create(payload) {
      return axios.post(workspaceRoute('quizzes/create'), payload);
    },
    update(id, payload) {
      return axios.patch(workspaceRoute(`quizzes/${id}/update`), payload);
    },
    getThread(id) {
      return axios.get(workspaceRoute(`quizzes/ai/threads/${id}/get`));
    },
    clearThread(id) {
      return axios.post(workspaceRoute(`quizzes/ai/threads/${id}/clear`));
    },
    sendMessageFeedback(id, payload) {
      return axios.post(workspaceRoute(`quizzes/ai/threads/${id}/feedback`), payload);
    },
    getVersions(id) {
      return axios.get(workspaceRoute(`quizzes/${id}/versions/get`));
    },
    getVersion(id, versionId) {
      return axios.get(workspaceRoute(`quizzes/${id}/versions/${versionId}/get`));
    }
  },
  templates: {
    getAll() {
      return axios.get('templates/get');
    },
    search({ page, limit, q }) {
      return axios.get('templates/search', { params: { page, limit, q } });
    },
  },
  quizTemplates: {
    getAll() {
      return axios.get(organizationRoute('templates/quizzes/get'));
    },
    get(id) {
      return axios.get(organizationRoute(`templates/quizzes/${id}/get`));
    },
    search({ page, limit, q }) {
      return axios.get(organizationRoute('templates/quizzes/search'), { params: { page, limit, q } });
    },
    create(payload) {
      return axios.post(organizationRoute('templates/quizzes/create'), payload);
    },
    update(id, payload) {
      return axios.patch(organizationRoute(`templates/quizzes/${id}/update`), payload);
    },
    delete(id) {
      return axios.delete(organizationRoute(`templates/quizzes/${id}/delete`));
    },
  },
  lander: {
    analytics: {
      getStats(id, { startDate, endDate, timezone }) {
        return axios.get(workspaceRoute(`landers/${id}/analytics/stats/get`), { params: { startDate, endDate, timezone } });
      },
      getGraphs(id, { startDate, endDate, timezone }) {
        return axios.get(workspaceRoute(`landers/${id}/analytics/graphs/get`), { params: { startDate, endDate, timezone } });
      },
      getReports(id, { startDate, endDate, timezone }) {
        return axios.get(workspaceRoute(`landers/${id}/analytics/reports/get`), { params: { startDate, endDate, timezone } });
      },
      getQuizzesStats(id, { startDate, endDate, timezone }) {
        return axios.get(workspaceRoute(`landers/${id}/analytics/quizzes/stats/get`), { params: { startDate, endDate, timezone } });
      }
    },
    variants: {
      getAll(id) {
        return axios.get(workspaceRoute(`landers/${id}/variants/get`));
      },
      enableAbTestMode(id) {
        return axios.post(workspaceRoute(`landers/${id}/variants/enable`));
      },
      disableAbTestMode(id) {
        return axios.post(workspaceRoute(`landers/${id}/variants/disable`));
      },
      setWeights(id, payload) {
        return axios.post(workspaceRoute(`landers/${id}/variants/setWeights`), payload);
      },
      create(id, payload) {
        return axios.post(workspaceRoute(`landers/${id}/variants/create`), payload);
      },
      update(id, variantId, payload) {
        return axios.put(workspaceRoute(`landers/${id}/variants/${variantId}/update`), payload);
      },
      uploadFavicon(id, variantId, payload) {
        return axios.post(workspaceRoute(`landers/${id}/variants/${variantId}/favicon/upload`), payload);
      },
      setMaster(id, variantId) {
        return axios.post(workspaceRoute(`landers/${id}/variants/${variantId}/setMaster`));
      },
      setIndex(id, variantId, payload) {
        return axios.post(workspaceRoute(`landers/${id}/variants/${variantId}/setIndex`), payload);
      },
      delete(id, variantId) {
        return axios.delete(workspaceRoute(`landers/${id}/variants/${variantId}/delete`));
      },
    },
    leads: {
      getAll(id, {
        variantId, startDate, endDate, timezone, page, limit, q, status
      }) {
        return axios.get(workspaceRoute(`landers/${id}/leads/get`), {
          params: {
            variantId, startDate, endDate, timezone, page, limit, q, status
          },
        });
      },
      sendToIntegrations(landerId, payload) {
        return axios.post(workspaceRoute(`landers/${landerId}/leads/export`), payload);
      },
    },
    integrations: {
      getIntegrationData(landerId, params) {
        return axios.get(workspaceRoute(`landers/${landerId}/integrations/getIntegrationData`), { params });
      },
      create(landerId, payload) {
        return axios.post(workspaceRoute(`landers/${landerId}/integrations/create`), payload);
      },
      update(landerId, integrationId, payload) {
        return axios.put(workspaceRoute(`landers/${landerId}/integrations/${integrationId}/update`), payload);
      },
      enableIntegration(landerId, integrationId) {
        return axios.post(workspaceRoute(`landers/${landerId}/integrations/${integrationId}/enable`));
      },
      disableIntegration(landerId, integrationId) {
        return axios.post(workspaceRoute(`landers/${landerId}/integrations/${integrationId}/disable`));
      },
      delete(landerId, integrationId) {
        return axios.delete(workspaceRoute(`landers/${landerId}/integrations/${integrationId}/delete`));
      },
    },
  },
  leads: {
    getAll({
      startDate, endDate, timezone, page, limit, q, landerIds, status
    }) {
      return axios.get(workspaceRoute('leads/get'), {
        params: {
          startDate, endDate, timezone, page, limit, q, landerIds, status
        },
      });
    },
    update(payload) {
      return axios.put(workspaceRoute('leads/update'), payload);
    },
    delete(payload) {
      return axios.post(workspaceRoute('leads/delete'), payload);
    },
  },
  domains: {
    getAll() {
      return axios.get(workspaceRoute('domains/get'));
    },
    check() {
      return axios.get(workspaceRoute('domains/check'));
    },
    create(payload) {
      return axios.post(workspaceRoute('domains/create'), payload);
    },
    delete(id) {
      return axios.delete(workspaceRoute(`domains/${id}/delete`));
    },
  },
  cloudflareAccounts: {
    getAll() {
      return axios.get(organizationRoute('cloudflare/get'));
    },
    create(payload) {
      return axios.post(organizationRoute('cloudflare/create'), payload);
    },
    // update(id, payload) {
    //   return axios.put(organizationRoute(`cloudflare/${id}/update`), payload);
    // },
    delete(id) {
      return axios.delete(organizationRoute(`cloudflare/${id}/delete`));
    },
  },
  website: {
    analytics: {
      getStats(id, { startDate, endDate, timezone }) {
        return axios.get(workspaceRoute(`websites/${id}/analytics/stats/get`), { params: { startDate, endDate, timezone } });
      },
      getGraphs(id, { startDate, endDate, timezone }) {
        return axios.get(workspaceRoute(`websites/${id}/analytics/graphs/get`), { params: { startDate, endDate, timezone } });
      },
      getReports(id, { startDate, endDate, timezone }) {
        return axios.get(workspaceRoute(`websites/${id}/analytics/reports/get`), { params: { startDate, endDate, timezone } });
      },
    },
  },
  editor: {
    lander: {
      get(id) {
        return axios.get(workspaceRoute(`editors/${id}/load`));
      },
      // preview(id, html) {
      //   return axios.post(`editor/preview/${id}`, { html });
      // },
      save(id, config, workspaceId) {
        return axios.post(workspaceRoute(`editors/${id}/save`, workspaceId), config);
      },
      // uploadFavicon(id, fd) {
      //   return axios.post(`editor/upload/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      // },
      loadFile(id, fileName) {
        return axios.get(workspaceRoute(`editors/${id}/loadFile`), { params: { fileName } });
      },
      saveFile(id, payload) {
        return axios.post(workspaceRoute(`editors/${id}/saveFile`), payload);
      },
      addFile(id, payload) {
        return axios.post(workspaceRoute(`editors/${id}/uploadFile`), payload);
      },
      deleteFile(id, payload) {
        return axios.post(workspaceRoute(`editors/${id}/deleteFile`), payload);
      },
      renameFile(id, payload) {
        return axios.post(workspaceRoute(`editors/${id}/renameFile`), payload);
      },
      generateTextAi(id, payload) {
        return axios.post(workspaceRoute(`editors/${id}/generate`), payload);
      },
      generateImageAi(id, payload) {
        return axios.post(workspaceRoute(`editors/${id}/image/generate`), payload, {
          responseType: 'arraybuffer'
        });
      },
      aiBgRemovalAccess(payload) {
        return axios.post(workspaceRoute('editors/remove-bg/access'), payload);
      },
      aiBgRemovalConsume(id) {
        return axios.get(workspaceRoute(`editors/${id}/remove-bg/consume`));
      },
      translate(id, payload) {
        return axios.post(workspaceRoute(`editors/${id}/translate`), payload);
      },
      getVersions(id) {
        return axios.get(workspaceRoute(`editors/${id}/versions/get`));
      },
      revertVersion(id, payload) {
        return axios.post(workspaceRoute(`editors/${id}/versions/revert`), payload);
      }
    },
    gallery: {
      get() {
        return axios.get(workspaceRoute('gallery/get'));
      },
      add(payload) {
        return axios.post(workspaceRoute('gallery/upload'), payload);
      },
      addFromUrl(payload) {
        return axios.post(workspaceRoute('gallery/url/upload'), payload);
      },
      delete(id) {
        return axios.delete(workspaceRoute(`gallery/${id}/delete`));
      },
      deleteMany(payload) {
        return axios.post(workspaceRoute('gallery/bulkDelete'), payload);
      },
    },
    config: {
      get() {
        return axios.get(workspaceRoute('editors/config/get'));
      },
    },
    admin: {
      getTemplate(id) {
        return axios.get(`admin/templates/${id}/load`);
      },
      saveTemplate(id, config) {
        return axios.post(`admin/templates/${id}/updateCode`, config);
      },
      loadFile(id, fileName) {
        return axios.get(`admin/templates/${id}/loadFile`, { params: { fileName } });
      },
      saveFile(id, payload) {
        return axios.post(`admin/templates/${id}/saveFile`, payload);
      },
    },
    icons: {
      getAll({
        page, limit, q, group, category, type
      }) {
        return axios.get(workspaceRoute('editors/icons'), {
          params: {
            page, limit, q, group, category, type
          },
        });
      },
      upload(payload) {
        return axios.post(workspaceRoute('editors/icons/upload'), payload);
      },
      delete(id) {
        return axios.delete(workspaceRoute(`editors/icons/${id}/delete`));
      },
    }
  },
  integrations: {
    getAll() {
      return axios.get(organizationRoute('integrations/get'));
    },
    create(payload) {
      return axios.post(organizationRoute('integrations/create'), payload);
    },
    delete(id) {
      return axios.delete(organizationRoute(`integrations/${id}/delete`));
    },
  },
  logs: {
    integrations: {
      getAll({
        startDate, endDate, timezone, page, limit, q, landerIds, type
      }) {
        return axios.get(organizationRoute('logs/landerIntegrations/get'), {
          params: {
            startDate, endDate, timezone, page, limit, q, landerIds, type
          },
        });
      },
    },
    verifications: {
      getAll({
        startDate, endDate, timezone, page, limit, q, landerIds, type
      }) {
        return axios.get(organizationRoute('logs/validations/get'), {
          params: {
            startDate, endDate, timezone, page, limit, q, landerIds, type
          },
        });
      },
    },
    credits: {
      getAll() {
        return axios.get(organizationRoute('credits/get'));
      },
    }
  },
  onboarding: {
    set(payload) {
      return axios.patch('users/onboarding', payload);
    }
  }
};

const apiProxy = (obj, path = '') => {
  Object.entries(obj).forEach(([key, func]) => { // eslint-disable-line
    if (typeof func !== 'function') return apiProxy(func, `${path}${path ? '.' : ''}${key}`); // func is not a function, deep recursive
    obj[key] = async (...args) => { // eslint-disable-line
      try {
        const result = await func(...args);
        if (process.env.NODE_ENV === 'development') {
          // console.log(`API ${path}${path ? '.' : ''}${key}, METHOD: ${result.config.method}, STATUS: ${result.status}, RESULT:`, result);
        }
        return result.data;
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          // TODO: uniform error
          if (err.response) {
            console.error(`API ${path}${path ? '.' : ''}${key}, METHOD: ${err.response.config.method}, STATUS: ${err.response.status}, ERROR:`, err);
          } else if (err.request) {
            console.error(`API ${path}${path ? '.' : ''}${key}, EMPTY_RESPONSE, ERROR:`, err);
          } else {
            console.error(`API ${path}${path ? '.' : ''}${key}, CONFIG_ERROR, ERROR:`, err);
          }
        }
        throw err;
      }
    };
  });
  return api;
};

export default apiProxy(api);
